import { CXoneVoiceClient, type CXoneVoiceConnectionOptions } from '@nice-devone/voice-sdk';
import { isCxoneDeepDebugEnabled } from '../../../utils/config';
import { logger } from '../../../utils/logger';
import {
  configureAudioCodesForFasterICE,
  installCallDebugInterceptors,
  installGetUserMediaInterceptor,
  preGatherICECandidates,
  preWarmMediaAccess,
  startConnectionWatcher,
  waitForSipRegistration,
} from '../voice-internals';
import type { VoiceConnectionOptions } from '../types';

export async function connectVoiceClient(
  agentId: string,
  options: VoiceConnectionOptions,
  audioElement: HTMLAudioElement,
  appName: string = 'SAP-NICE Agent Widget'
): Promise<void> {
  logger.lifecycle('[CXone] Voice connection started', { agentId });
  logger.debug('[CXone SDK] Voice connection options:', options);

  try {
    logger.debug('[CXone SDK] Step 1: Pre-warming media access...');
    await preWarmMediaAccess();
    logger.debug('[CXone SDK] Step 1: Media pre-warmed successfully');

    logger.debug('[CXone SDK] Step 2: Installing getUserMedia interceptor...');
    installGetUserMediaInterceptor();

    preGatherICECandidates(options.webRTCIceUrls).catch((err) => {
      logger.warn('[CXone SDK] ICE pre-gathering failed (non-blocking):', err);
    });

    configureAudioCodesForFasterICE();

    const enableDeepDebug = isCxoneDeepDebugEnabled();
    if (enableDeepDebug) {
      installCallDebugInterceptors();
    }

    const voiceClient = CXoneVoiceClient.instance;

    const connectionOptions: CXoneVoiceConnectionOptions = {
      webRTCType: options.webRTCType || '',
      webRTCWssUrls: options.webRTCWssUrls || [],
      webRTCServerDomain: options.webRTCServerDomain || '',
      webRTCIceUrls: options.webRTCIceUrls || [],
      webRTCDnis: options.webRTCDnis || '',
      noiseCancellation: options.noiseCancellation,
      echoCancellation: options.echoCancellation,
    };

    logger.debug('[CXone SDK] Step 4: Calling connectServer with options:', connectionOptions);
    voiceClient.connectServer(agentId, connectionOptions, audioElement, appName);

    if (enableDeepDebug) {
      (window as unknown as { __voiceClient: typeof voiceClient }).__voiceClient = voiceClient;
      logger.debug('[CXone SDK] Voice client exposed as window.__voiceClient for debugging');
      startConnectionWatcher();
    }

    logger.debug('[CXone SDK] Voice client connection initiated');

    try {
      await waitForSipRegistration(15000);
      logger.lifecycle('[CXone] Voice connection ready');
    } catch (regError) {
      logger.warn('[CXone SDK] SIP registration did not complete, proceeding anyway:', regError);
    }
  } catch (error) {
    logger.error('[CXone] Voice connection failed', error);
    throw error;
  }
}

export function disconnectVoiceClient(): void {
  logger.lifecycle('[CXone] Voice disconnect started');

  try {
    const voiceClient = CXoneVoiceClient.instance;
    voiceClient.disconnectServer();
    logger.lifecycle('[CXone] Voice disconnect complete');
  } catch (error) {
    logger.error('[CXone] Voice disconnect failed', error);
  }
}
